/**
 * Building info panel — floating PixiJS panel shown when a building is selected.
 * Displays building name, HP, and for TransporterStation a route config button.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { BuildingKind, BuildingState } from './client_message_protocol';

const BUILDING_NAMES: Record<BuildingKind, string> = {
  [BuildingKind.SpiritRoot]: '灵根',
  [BuildingKind.GatheringArray]: '采集阵',
  [BuildingKind.RefiningArray]: '精炼阵',
  [BuildingKind.AlchemyFurnace]: '炼丹炉',
  [BuildingKind.Forge]: '锻造台',
  [BuildingKind.PuppetWorkshop]: '傀儡工坊',
  [BuildingKind.DefenseTower]: '防御塔',
  [BuildingKind.SwordTower]: '剑塔',
  [BuildingKind.SlowTrap]: '缓速陷阱',
  [BuildingKind.PuppetBarracks]: '傀儡兵营',
  [BuildingKind.TransporterStation]: '搬运站',
  [BuildingKind.HerbFarm]: '药田',
};

const PANEL_W = 180;
const PANEL_H_TALL = 100; // with config button
const PANEL_H_SHORT = 64; // without config button
const BG_COLOR = 0x1a1a2e;
const BG_ALPHA = 0.9;
const TEXT_COLOR = 0xdddddd;
const CONFIG_BTN_COLOR = 0x336633;

export class UiBuildingInfoPanel {
  private container: Container;
  private bg: Graphics;
  private nameText: Text;
  private hpText: Text;
  private configBtn: Container;
  private configBtnText: Text;
  private _visible = false;
  private _configBtnBounds = { x: 0, y: 0, w: 0, h: 0 };

  constructor(uiLayer: Container) {
    this.container = new Container();
    this.container.visible = false;

    this.bg = new Graphics();
    this.container.addChild(this.bg);

    this.nameText = new Text({
      text: '',
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 13, fill: 0xffffff, fontWeight: 'bold' }),
      resolution: 2,
      roundPixels: true,
    });
    this.container.addChild(this.nameText);

    this.hpText = new Text({
      text: '',
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: TEXT_COLOR }),
      resolution: 2,
      roundPixels: true,
    });
    this.container.addChild(this.hpText);

    // Config button (initially hidden)
    this.configBtn = new Container();
    this.configBtn.visible = false;
    const btnBg = new Graphics();
    btnBg.roundRect(0, 0, PANEL_W - 20, 22, 4);
    btnBg.fill({ color: CONFIG_BTN_COLOR, alpha: 1 });
    this.configBtn.addChild(btnBg);

    this.configBtnText = new Text({
      text: '⚙ 配置路线',
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: 0x88ff88 }),
      resolution: 2,
      roundPixels: true,
    });
    this.configBtnText.anchor.set(0.5, 0.5);
    this.configBtnText.x = (PANEL_W - 20) / 2;
    this.configBtnText.y = 11;
    this.configBtn.addChild(this.configBtnText);

    this.container.addChild(this.configBtn);
    uiLayer.addChild(this.container);
  }

  show(building: BuildingState, screenX: number, screenY: number): void {
    const isTransporter = building.kind === BuildingKind.TransporterStation;
    const panelH = isTransporter ? PANEL_H_TALL : PANEL_H_SHORT;

    // Clamp to viewport bounds
    const cx = Math.max(8, Math.min(screenX - PANEL_W / 2, window.innerWidth - PANEL_W - 8));
    const cy = Math.max(8, Math.min(screenY, window.innerHeight - panelH - 8));

    this.container.x = cx;
    this.container.y = cy;

    // Background
    this.bg.clear();
    this.bg.roundRect(0, 0, PANEL_W, panelH, 6);
    this.bg.fill({ color: BG_COLOR, alpha: BG_ALPHA });

    // Name
    this.nameText.text = BUILDING_NAMES[building.kind] ?? building.kind;
    this.nameText.x = 8;
    this.nameText.y = 8;

    // HP with color
    const hpRatio = building.max_hp > 0 ? building.hp / building.max_hp : 0;
    const hpColor = hpRatio > 0.5 ? '#44ff44' : hpRatio > 0.25 ? '#ffcc44' : '#ff4444';
    this.hpText.text = `HP: ${Math.ceil(building.hp)}/${building.max_hp}`;
    this.hpText.style.fill = hpColor;
    this.hpText.x = 8;
    this.hpText.y = 28;

    // Config button for TransporterStation
    if (isTransporter) {
      this.configBtn.visible = true;
      this.configBtn.x = 10;
      this.configBtn.y = 52;
      this._configBtnBounds = {
        x: cx + 10,
        y: cy + 52,
        w: PANEL_W - 20,
        h: 22,
      };
    } else {
      this.configBtn.visible = false;
      this._configBtnBounds = { x: 0, y: 0, w: 0, h: 0 };
    }

    this._visible = true;
    this.container.visible = true;
  }

  hide(): void {
    this._visible = false;
    this.container.visible = false;
  }

  isVisible(): boolean {
    return this._visible;
  }

  getConfigButtonHit(screenX: number, screenY: number): boolean {
    if (!this._visible || !this.configBtn.visible) return false;
    const b = this._configBtnBounds;
    return screenX >= b.x && screenX <= b.x + b.w &&
           screenY >= b.y && screenY <= b.y + b.h;
  }
}
